import ReactPaginate from "react-paginate";
import { useEffect, useRef, useState } from "react";
import DateConverter from "@heartinz/date_and_time_processor";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";
import AxiosInstance from "../../../utils/AxiosInstance";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import GetServerLiveTime from "../../../constants/GetServerLiveTime";
import { useShowAlert } from "../../../context/AlertContext";
import { useNetwork } from "../../../context/NetworkContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthProvider";
import { usePermission } from "../../../context/PermissionContext";
import Table from "../../../constants/Table";
import { CircularProgress } from "@mui/material";
import uRLs from "../../../utils/baseUtils";
import InvoiceTempPreview from "../settings/Invoice/InvoiceTempPreview";
import useKeyboardTableNavigation from "../../../constants/useKeyboardTableNavigation";
import AddSaleProductProforma from "./AddSaleProductProforma";
import TemplateModalLayout from "../../../templates/modules/TemplateModalLayout";
import { FilterAltIcon, SearchIcon } from "../../../constants/Icons";

const AddSaleProduct = ({ isActive, onClose, onAddToCart }) => {
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaleData, setSaleData] = useState([]);
  const serverTime = GetServerLiveTime();
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const SaleSuggestionsRef = useRef(null);
  const [isSearchTerm, setSearchTerm] = useState("");
  const [SaleSuggestions, setSaleSuggestions] = useState([]);
  const [selectedSaleNo, setSelectedSaleNo] = useState("");
  const [suggestionLoading, setSuggestionLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 10;
  const [isReportMode, setIsReportMode] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isViewData, setViewData] = useState(null);
  const [isDeleteData, setDeleteData] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionRef = useRef([]);
  const [showSaleDropdown, setShowSaleDropdown] = useState(false);
  const [buttonLoading, setButtonLoading] = useState({
    type: "",
    id: null,
  });
  const { permission, blockedPermissions, globelPopupOpen, storeSettings } =
    usePermission();
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const { isUser, updateSettings } = useAuth();
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);
  const [selectedFilter, setSelectedFilter] = useState("today");
  const previousDate = new Date(serverTime.getTime() - 1000);
  const [filterFromDate, setFilterFromDate] = useState(null);
  const [filterToDate, setFilterToDate] = useState(null);
  const [fromDate, setFromDate] = useState(previousDate);
  const [toDate, setToDate] = useState(serverTime);
  const [selectTermalOption, setSelectTermalOption] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const templateId = storeSettings?.invoiceSettings?.template?.templateId;
  const [isViewTempModalOpen, setViewTempModalOpen] = useState(false);
  const [AddproductOpen, setAddproductOpen] = useState(false);

  const handleListSale = async (page = 0, term = "") => {
    try {
      if (!checkNetwork()) return;
      setIsLoading(true);
      const data = { searchtxt: term, pageno: page + 1, perpage: perPage };
      const response = await AxiosInstance.get(
        `module/sale?pageno=${page + 1}&limit=${perPage}&searchtxt=${term}`,
      );
      const results = response.data.results || [];
      setSaleData(results);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterSaleList = async (
    page = 0,
    from = fromDate,
    to = toDate,
  ) => {
    setIsReportMode(true);
    setIsLoading(true);
    try {
      if (!checkNetwork()) return;

      let query = `module/sale/date-list?pageno=${page + 1}&limit=${perPage}`;

      if (from && to) {
        const data = {
          fromdate: encodeURIComponent(
            DateConverter.convertToTimeZone(
              from.toISOString(),
              isUser.timezone,
            ),
          ),
          todate: encodeURIComponent(
            DateConverter.convertToTimeZone(to.toISOString(), isUser.timezone),
          ),
        };
        query += `&startdate=${data.fromdate}&enddate=${data.todate}`;
      }

      const response = await AxiosInstance.get(query);
      const results = response.data.results || [];
      setSaleData(results);

      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalItems(response.data.totalItems || 0);
        setTotalPages(response.data.totalPages || 0);
        setCurrentPage(page);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickFilter = (filter, page = 0) => {
    setSelectedFilter(filter);

    if (filter === "allTime") {
      // All Time: unfiltered API
      setIsReportMode(false);
      setFilterFromDate(null);
      setFilterToDate(null);
      setFromDate(null);
      setToDate(null);
      handleListSale(page, isSearchTerm);
      return;
    }

    // Filtered ranges
    let startDate, endDate;

    if (filter === "today") {
      const now = serverTime;
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
    } else if (filter === "yesterday") {
      const yesterday = serverTime;
      yesterday.setDate(yesterday.getDate() - 1);

      startDate = new Date(yesterday);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(yesterday);
      endDate.setHours(23, 59, 59, 999);
    } else {
      ({ startDate, endDate } = DateConverter.getDateRange(filter));
      startDate = new Date(startDate);
      endDate = new Date(endDate);
    }

    setFilterFromDate(startDate);
    setFilterToDate(endDate);
    setFromDate(startDate);
    setToDate(endDate);

    setIsReportMode(true);
    handleFilterSaleList(page, startDate, endDate);
  };

  const handleSaleItems = async (saleId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`module/sale/${saleId}`);
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const handleRefresh = async () => {
    setCurrentPage(0);

    // Keep same filter on refresh
    if (selectedFilter && selectedFilter !== "allTime") {
      await handleFilterSaleList(0, filterFromDate, filterToDate);
    } else {
      await handleListSale(0, isSearchTerm);
    }
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    setHasSearched(true);
    setSelectedSaleNo("");
    if (!term.trim()) {
      setSaleSuggestions([]);

      // ✅ IMPORTANT FIX
      setSelectedFilter("allTime");
      setIsReportMode(false);

      await handleListSale(0, "");
      setCurrentPage(0);
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        if (!checkNetwork()) return;
        setSuggestionLoading(true);
        const response = await AxiosInstance.get(
          `module/sale?searchtxt=${term}&salemode=INVOICE`,
        );
        setSaleSuggestions(response.data.results || []);
      } catch (error) {
        handleError(error, showAlert);
        return false;
      } finally {
        setSuggestionLoading(false);
      }
    }, 200);
  };

  const handleSearchFind = () => {
    const currentInputValue = searchInputRef.current?.value || "";
    setSearchTerm(currentInputValue);
    setSelectedSaleNo("");

    if (!currentInputValue.trim()) {
      // ✅ IMPORTANT FIX
      setSelectedFilter("allTime"); // reset filter
      setIsReportMode(false);

      handleListSale(0, "");
    } else {
      handleListSale(0, currentInputValue);
    }

    setSaleSuggestions([]);
    setHasSearched(false);
    setCurrentPage(0);
  };

  const handleSelectSale = (sale) => {
    const saleno = sale.saleno;
    setSearchTerm(saleno);
    setSelectedSaleNo(saleno);
    setSaleSuggestions([]);
    setHasSearched(false);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    handleListSale(0, saleno);
    setCurrentPage(0);
  };

  const handleSetTemplate = async (
    templateId,
    selectTermalOption,
    selectHalfA4Option,
    templateTheme,
    show = true,
  ) => {
    if (!checkNetwork()) return;
    try {
      const response = await AxiosInstance.patch(
        `module/storesettings/template`,
        {
          templateId: `${templateId}AND${selectTermalOption}AND${selectHalfA4Option}`,
          templateTheme,
        },
      );
      const results = response.data?.results?.invoiceSettings?.template;
      const id = results.templateId;
      const words = id.split("AND");
      setSelectedOption(words[0]);
      setSelectTermalOption(words[1]);
      setViewTempModalOpen(false);
      updateSettings({
        storeSettings: {
          invoiceSettings: {
            template: {
              templateId: results?.templateId,
              templateTheme: results?.templateTheme,
            },
            signature: results?.signature,
            termsAndConditions: results?.termsAndConditions,
          },
        },
      });
      {
        show && showAlert("success", "Template Selected...");
      }
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const handleView = async (item) => {
    setButtonLoading({ type: "view", id: item._id });
    try {
      const data = await handleSaleItems(item._id);
      if (data) {
        setViewData(data);
        setAddproductOpen(true);
      }
    } catch (error) {
      console.error("Error fetching view data:", error);
    } finally {
      setButtonLoading({ type: "", id: null });
    }
  };

  const handleClose = () => {
    setViewModalOpen(false);
    setViewData(null);
    navigate(location.pathname, { replace: true, state: {} });
  };

  const handleEdit = async (item) => {
    if (blockedPermissions("SALE:update") || permission === undefined) {
      setButtonLoading({ type: "edit", id: item._id });

      try {
        const data = await handleSaleItems(item._id);

        if (data) {
          if (data.consumeVersion === "CONSUMEV2") {
            navigate("/editsale", { state: data });
          } else {
            navigate("/editsaleold", { state: data });
          }
        }
      } catch (error) {
        console.error("Error loading sale data:", error);
      } finally {
        setButtonLoading({ type: "", id: null });
      }
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const openDeleteModal = (cItem) => {
    if (blockedPermissions("SALE:delete") || permission === undefined) {
      setDeleteData(cItem);
      setDeleteModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleDelete = async () => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      var data = isDeleteData._id;
      const response = await AxiosInstance.delete(`module/sale/${data}`);
      showAlert("success", Messages.SUCCESS.SALE_DELETED);

      const newTotal = totalItems - 1;
      const newTotalPages = Math.ceil(newTotal / perPage);

      if (isSaleData.length === 1 && currentPage > 0) {
        setCurrentPage((prev) => prev - 1);
        await handleListSale(currentPage - 1, isSearchTerm);
      } else {
        await handleListSale(currentPage, isSearchTerm);
      }

      setTotalItems(newTotal);
      setTotalPages(newTotalPages);
      setDeleteModalOpen(false);

      closeDeleteModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteData(null);
    setDeleteModalOpen(false);
  };

  const handleOutsideClick = (e) => {
    if (
      SaleSuggestionsRef.current &&
      !SaleSuggestionsRef.current.contains(e.target)
    ) {
      setSaleSuggestions([]);
      setHasSearched(false);
    }
  };

  const getColor = (saleTrack, status) => {
    switch (saleTrack?.toUpperCase()) {
      case "ADDED":
        return "bg-label-success"; // Green
      case "UPDATED":
        return "bg-label-warning"; // Yellow
      case "DELETED":
        return "bg-label-danger"; // Red
      case "RETURNED":
        return "bg-label-info"; // Blue
      default:
        switch (status?.toLowerCase()) {
          case "active":
            return "bg-label-success";
          case "cancelled":
            return "bg-label-danger";
          default:
            return "bg-label-secondary";
        }
    }
  };

  const getStatusLabel = (saleTrack, status) => {
    switch (saleTrack?.toUpperCase()) {
      case "ADDED":
        return "Original";
      case "UPDATED":
        return "Modified";
      case "DELETED":
        return "Cancelled";
      case "RETURNED":
        return "Returned";
      default:
        return status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase();
    }
  };

  const formatTrackMode = (salemode) => {
    switch (salemode?.toUpperCase()) {
      case "BILL":
        return "BILL RECIEPT";
      case "INVOICE":
        return "TAX INVOICE";
      default:
        return salemode;
    }
  };

  const getBgColor = (salemode) => {
    if (!salemode || typeof salemode !== "string") return "bg-label-secondary";
    switch (salemode.toLowerCase()) {
      case "bill":
        return "bg-label-honey";
      case "invoice":
        return "bg-label-moon";
      default:
        return "bg-label-secondary";
    }
  };

  const getBgTxtColor = (salemode) => {
    if (!salemode || typeof salemode !== "string") return "bg-label-secondary";
    switch (salemode.toLowerCase()) {
      case "bill":
        return "bg-label-wave";
      case "invoice":
        return "bg-label-deep-purple";
      default:
        return "bg-label-secondary";
    }
  };

  const handleLink = () => {
    if (blockedPermissions("SALE:add") || permission === undefined) {
      navigate("/sale");
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleProductKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, SaleSuggestions.length - 1),
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0 && SaleSuggestions[highlightedIndex]) {
        e.preventDefault();
        handleSelectSale(SaleSuggestions[highlightedIndex]);
      } else {
        handleSearchFind();
        setShowSaleDropdown(false);
        setHighlightedIndex(-1);
      }
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionRef.current[highlightedIndex]) {
      suggestionRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  // useEffect(() => {
  //   handleListSale(currentPage, isSearchTerm);
  // }, [currentPage]);

  // useEffect(() => {
  //   if (!isReportMode) {
  //     handleListSale(currentPage, isSearchTerm);
  //   }
  // }, [currentPage]);

  useEffect(() => {
    const id = storeSettings?.invoiceSettings?.template?.templateId || "";
    const words = id ? id?.split("AND") : [];
    setSelectedOption(words[0]);
    setSelectTermalOption(words[1]);
  }, []);

  useKeyboardTableNavigation({
    isActive,
    containerRef,
    searchTerms: isSearchTerm,
    suggestions: SaleSuggestions,
    modals: {
      isViewModalOpen,
      isDeleteModalOpen,
      globelPopupOpen,
    },
    onEnter: handleView,
  });

  useEffect(() => {
    if (!isActive) return;
    if (!isViewModalOpen) {
      // small timeout ensures DOM is visible after tab switch
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [isActive]);

  useEffect(() => {
    handleQuickFilter("today", 0);
  }, []);

  useEffect(() => {
    if (currentPage === 0) return;
    if (selectedFilter && selectedFilter !== "allTime") {
      handleFilterSaleList(currentPage, filterFromDate, filterToDate);
    } else {
      handleListSale(currentPage, isSearchTerm);
    }
  }, [currentPage]);

  useEffect(() => {
    if (location.state?.modalData) {
      setViewData(location.state.modalData);
      setViewModalOpen(true);
      setViewTempModalOpen(true);
    }
  }, [location.state]);
  return (
    <div className="modal-overlay" style={{ zIndex: 1010 }}>
      <div className="modal-backdrop show"></div>

      <div
        className="modal show"
        id="JObCardModal"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered ">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title me-2">List sale</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body bg-light ">
              <div className="row">
                <div className="col">
                  <div className="card radius-10 mb-0">
                    <div className="card-body px-0">
                      <div className="row px-3 align-items-center">
                        {/* Search input */}
                        <div className="col-lg-4 col-12 mb-2 mb-lg-0">
                          <div className="position-relative">
                            <div
                              className="input-group"
                              ref={SaleSuggestionsRef}
                            >
                              <input
                                type="text"
                                className="form-control"
                                ref={searchInputRef}
                                onChange={(e) => {
                                  handleSearch(e.target.value);
                                  setShowSaleDropdown(true);
                                  setHighlightedIndex(-1);
                                }}
                                onKeyDown={handleProductKeyDown}
                                onFocus={() => {
                                  if (SaleSuggestions.length > 0)
                                    setShowSaleDropdown(true);
                                }}
                                placeholder="Search sale by saleno..."
                                autoFocus
                              />
                              <button
                                className="input-group-text"
                                onClick={handleSearchFind}
                              >
                                <SearchIcon/>
                              </button>
                            </div>

                            {hasSearched &&
                              isSearchTerm &&
                              !suggestionLoading &&
                              SaleSuggestions.length === 0 && (
                                <ul className="suggestions-list">
                                  <li className="suggestion-item text-muted">
                                    No Sales data was found
                                  </li>
                                </ul>
                              )}

                            {showSaleDropdown && SaleSuggestions.length > 0 && (
                              <ul className="suggestions-list">
                                {SaleSuggestions.map((item, index) => (
                                  <li
                                    ref={(e) =>
                                      (suggestionRef.current[index] = e)
                                    }
                                    key={item._id}
                                    onClick={() => handleSelectSale(item)}
                                    className={`suggestion-item ${
                                      index === highlightedIndex
                                        ? "highlighted"
                                        : ""
                                    }`}
                                  >
                                    <span className="badge rounded-pill bg-label-moon">
                                      {item.salemode}
                                    </span>{" "}
                                    -{" "}
                                    <span className="badge rounded-pill bg-label-star">
                                      {item.saleno}
                                    </span>{" "}
                                    -{" "}
                                    <span className="text-nowrap">
                                      {item.saleto ? item.saleto.name : ""}{" "}
                                      <small>
                                        +91{" "}
                                        {item.saleto ? item.saleto.phone : ""}
                                      </small>
                                    </span>{" "}
                                    -{" "}
                                    <span className="badge bg-label-fall rounded-pill">
                                      ₹
                                      {CurrencyConverter.toCurrency(
                                        item.grandtotal || 0,
                                        "INR",
                                      )}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>

                        {/* Filter dropdown */}
                        <div className="col-lg-2 col-6 mb-2 mb-lg-0">
                          <div className="input-group">
                            <span className="input-group-text">
                              <FilterAltIcon/>
                            </span>
                            <select
                              className="form-select"
                              onChange={(e) =>
                                handleQuickFilter(e.target.value)
                              }
                              value={selectedFilter} // today will be selected by default
                            >
                              <option value="today">Today</option>
                              <option value="yesterday">Yesterday</option>
                              <option value="thisWeek">This Week</option>
                              <option value="lastWeek">Last Week</option>
                              <option value="thisMonth">This Month</option>
                              <option value="lastMonth">last Month</option>
                              <option value="allTime">All Time</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-lg-2 col-md-3 col-6 mb-2 mb-lg-0">
                          <strong>Total Sales:</strong>{" "}
                          <span className="badge btn-label-primary fs-15 ">
                            {totalItems || 0}
                          </span>
                        </div>
                        {/* <div className="col-lg-4 col-md-3 col-12 d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleLink}
                          >
                            <i className="ng ng-ng-add-icon"></i> Add Sale
                          </button>
                        </div> */}
                      </div>
                      <div ref={containerRef}>
                        <Table
                          loading={isLoading}
                          data={isSaleData}
                          onRowClick={(item) => handleView(item)}
                          columns={[
                            {
                              label: "S.No",
                              render: (_, index) => (
                                <>
                                  {totalItems - (currentPage * perPage + index)}
                                </>
                              ),
                            },
                            {
                              label: "Sale Mode",
                              render: (item) =>
                                item.salemode && (
                                  <span
                                    className={`badge rounded-pill ${getBgColor(
                                      item.salemode,
                                    )}`}
                                  >
                                    {formatTrackMode(item.salemode)}
                                  </span>
                                ),
                            },
                            {
                              label: "Sale No",
                              render: (item) =>
                                item.salemode && (
                                  <span
                                    className={`badge rounded-pill ${getBgTxtColor(
                                      item.salemode,
                                    )}`}
                                  >
                                    {item.saleno}
                                  </span>
                                ),
                            },
                            {
                              label: "Sale Date",
                              render: (item) => (
                                <div className="d-flex flex-column">
                                  <span className="text-nowrap text-heading fw-medium">
                                    {
                                      DateConverter.ISOToDisplayFormat(
                                        DateConverter.convertToTimeZone(
                                          item.saledate,
                                          isUser.timezone,
                                        ),
                                      ).date
                                    }
                                  </span>
                                  {/* <span className="text-nowrap">
                                           <small>
                                             {
                                               DateConverter.ISOToDisplayFormat(
                                                 DateConverter.convertToTimeZone(
                                                   item.saledate,
                                                   isUser.timezone,
                                                 ),
                                               ).time
                                             }
                                           </small>
                                         </span> */}
                                </div>
                              ),
                            },
                            {
                              label: "Sale To",
                              render: (item) => (
                                <div className="d-flex flex-column">
                                  <span className="text-nowrap text-heading fw-medium">
                                    {item.saleto ? `${item.saleto.name}` : ""}
                                  </span>
                                  <span className="text-nowrap">
                                    <small>
                                      {item.saleto
                                        ? `+91 ${item.saleto.phone}`
                                        : " - "}
                                    </small>
                                  </span>
                                </div>
                              ),
                            },
                            {
                              label: "Sale Items",
                              render: (item) => (
                                <>{item?.totalItems || 0} Item(s)</>
                              ),
                            },
                            {
                              label: "Grand Total",
                              render: (item) => (
                                <span className="text-heading fw-medium text-truncate">
                                  ₹
                                  {CurrencyConverter.toCurrency(
                                    Number(item.grandtotal) || 0,
                                    "INR",
                                  )}
                                </span>
                              ),
                            },
                            {
                              label: "Status",
                              render: (item) => (
                                <span
                                  className={`badge rounded-pill ${getColor(
                                    item.saleTrack,
                                    item.status,
                                  )}`}
                                >
                                  {getStatusLabel(item.saleTrack, item.status)}
                                </span>
                              ),
                            },
                            // {
                            //   label: "Actions",
                            //   render: (item) => (
                            //     <div className="order-actions opacity-0 opacity-md-100">
                            //       <button
                            //         type="button"
                            //         className="btn-label-info shadow-none me-2"
                            //         onClick={(e) => {
                            //           e.stopPropagation();
                            //           handleView(item);
                            //         }}
                            //         disabled={
                            //           buttonLoading.id === item._id &&
                            //           buttonLoading.type === "view"
                            //         }
                            //       >
                            //         {buttonLoading.id === item._id &&
                            //         buttonLoading.type === "view" ? (
                            //           <CircularProgress
                            //             size={20}
                            //             className="text-primary"
                            //           />
                            //         ) : (
                            //           <i className="bx bx-show"></i>
                            //         )}
                            //       </button>
                            //       {item?.status === "ACTIVE" &&
                            //         item?.saleTrack !== "RETURNED" && (
                            //           <button
                            //             type="button"
                            //             className="btn-label-primary shadow-none me-2"
                            //             onClick={(e) => {
                            //               e.stopPropagation();
                            //               handleEdit(item);
                            //             }}
                            //             disabled={
                            //               buttonLoading.id === item._id &&
                            //               buttonLoading.type === "edit"
                            //             }
                            //           >
                            //             {buttonLoading.id === item._id &&
                            //             buttonLoading.type === "edit" ? (
                            //               <CircularProgress
                            //                 size={20}
                            //                 className="text-primary"
                            //               />
                            //             ) : (
                            //               <i className="ng ng-ng-edit-icon"></i>
                            //             )}
                            //           </button>
                            //         )}
                            //       {(item?.status === "ACTIVE" ||
                            //         item?.status === "CANCELLED") &&
                            //         item?.saleTrack !== "DELETED" &&
                            //         item?.saleTrack !== "RETURNED" && (
                            //           <button
                            //             type="button"
                            //             className="btn-label-danger shadow-none me-2"
                            //             onClick={(e) => {
                            //               e.stopPropagation();
                            //               openDeleteModal(item);
                            //             }}
                            //           >
                            //             <i className="ng ng-ng-delete-icon"></i>
                            //           </button>
                            //         )}
                            //       {uRLs.RETURN_SALE === "true" &&
                            //         item?.status === "ACTIVE" && (
                            //           <button
                            //             type="button"
                            //             className="btn-label-wave shadow-none me-2"
                            //             onClick={(e) => {
                            //               e.stopPropagation();

                            //               navigate("/returnsale", {
                            //                 state: {
                            //                   saleId: item?._id,
                            //                 },
                            //               });
                            //             }}
                            //           >
                            //             <i className="bx bx-undo"></i>
                            //           </button>
                            //         )}
                            //     </div>
                            //   ),
                            // },
                          ]}
                        />
                      </div>
                      <div className="row align-items-center mt-3">
                        <div className="col text-center">
                          {totalPages > 0 && (
                            <ReactPaginate
                              previousLabel={"← Previous"}
                              nextLabel={"Next →"}
                              breakLabel={"..."}
                              onPageChange={({ selected }) => {
                                setIsLoading(true);
                                setCurrentPage(selected);

                                if (
                                  selectedFilter &&
                                  selectedFilter !== "allTime"
                                ) {
                                  handleFilterSaleList(
                                    selected,
                                    filterFromDate,
                                    filterToDate,
                                  );
                                } else {
                                  handleListSale(selected, isSearchTerm);
                                }
                              }}
                              pageRangeDisplayed={5}
                              pageCount={totalPages}
                              containerClassName="pagination justify-content-center mb-0"
                              pageClassName="page-item"
                              pageLinkClassName="page-link"
                              previousClassName="page-item"
                              previousLinkClassName="page-link"
                              nextClassName="page-item"
                              nextLinkClassName="page-link"
                              breakClassName="page-item"
                              breakLinkClassName="page-link"
                              activeClassName="active"
                              forcePage={currentPage}
                            />
                          )}
                        </div>
                        {/* <div className="col-auto text-end me-4">
                                 <strong>Total Items:</strong> {totalItems || 0}
                               </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {!templateId && isViewTempModalOpen && (
          <InvoiceTempPreview
            isData={isViewData}
            onClose={() => setViewTempModalOpen(false)}
            onAdd={handleSetTemplate}
          />
        )}
        {templateId && isViewModalOpen && (
       <TemplateModalLayout
                    isOpen={isViewModalOpen}
                    isData={isViewData}
                    onClose={handleClose}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    setSaleData={setViewData}
                    isActive={isActive}
                  />         
        )}
        {AddproductOpen && (
          <AddSaleProductProforma
            data={isViewData}
            onClose={() => setAddproductOpen(false)}
            onAdd={onAddToCart}
          />
        )}
      </div>
    </div>
  );
};

export default AddSaleProduct;
